import { cn } from '@cannect/lib/utils'
import { Link } from '@cannect/new-components/atoms'
import { LuArrowRightCircle } from 'react-icons/lu'
import { type VariantProps, tv } from 'tailwind-variants'

/**
 * Pill de CTA das seções do Novo Marketplace (ex.: Figma node 4070:12253 / 4070:11973).
 *
 * O protótipo repete essa mesma geometria em várias seções — hero, "Como funciona",
 * "Qual é o próximo passo", Cannect Cuida, aplicativo — sempre como frame solto e sem estados.
 * Aqui ela é um componente único com variantes; hover, foco visível e disabled foram supridos.
 *
 * Medidas do design: padding 24x8, raio total, texto 14px/1.4, ícone de seta 16px.
 * Não confundir com `HeaderPill`, que é menor (25px de altura, 12px) e serve à navegação do header.
 */
const ctaPill = tv({
  base: [
    'inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-full',
    'border border-solid px-6 py-2 font-sans text-sm font-normal leading-[1.4] no-underline transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:opacity-60'
  ],
  variants: {
    variant: {
      /** Ação principal: verde claro preenchido. Usar sobre fundo claro ou escuro. */
      solid:
        'border-marketplace-600 bg-marketplace-500 text-neutral-900 hover:bg-marketplace-600 focus-visible:ring-marketplace-600 focus-visible:ring-offset-transparent',
      /**
       * Verde escuro do arquivo (`marketplace-850`) com contorno claro. Repete-se na busca da
       * descoberta, no Cannect Cuida e no aplicativo — daí ser variante, e não `className` solto.
       *
       * O arquivo põe o rótulo em `marketplace-500`, que rende 3,73:1 sobre esse fundo e reprova
       * WCAG AA. Aqui é `neutral-50`, 7,18:1.
       */
      solidDeep:
        'border-marketplace-500 bg-marketplace-850 text-neutral-50 hover:bg-marketplace-900 focus-visible:ring-marketplace-500 focus-visible:ring-offset-transparent',
      /** Ação principal em verde escuro, para uso sobre fundo claro (ex.: card branco). */
      solidDark:
        'border-marketplace-900 bg-marketplace-800 text-neutral-50 hover:bg-marketplace-900 focus-visible:ring-marketplace-800',
      /** Ação secundária sobre fundo escuro: contorno claro. */
      outlineLight:
        'border-neutral-50 bg-transparent text-neutral-50 hover:bg-neutral-50/10 focus-visible:ring-neutral-50 focus-visible:ring-offset-transparent',
      /** Ação secundária sobre verde escuro: contorno e texto na rampa, sem branco puro. */
      outlineSoft:
        'border-marketplace-800 bg-transparent text-marketplace-500 hover:bg-marketplace-900 focus-visible:ring-marketplace-500 focus-visible:ring-offset-transparent',
      /** Ação secundária sobre fundo claro: contorno neutro. */
      outlineDark:
        'border-neutral-200 bg-transparent text-marketplace-800 hover:bg-marketplace-100 focus-visible:ring-marketplace-800 focus-visible:ring-offset-transparent'
    },
    full: {
      true: 'w-full'
    }
  },
  defaultVariants: { variant: 'solid', full: false }
})

export type TCtaPillProps = VariantProps<typeof ctaPill> & {
  title: string
  path?: string
  external?: boolean
  onClick?: () => void
  /** O design sempre traz a seta; deixe `false` para omitir. */
  withArrow?: boolean
  /**
   * Ilustração de 48px à esquerda do rótulo (ex.: nodes 4619:522 e 4641:275). Quando presente, o
   * pill vira retangular com 8px de padding — é a geometria do arquivo para essa variação — e o
   * rótulo se afasta da seta, que passa a ancorar no fim.
   *
   * O grupo do rótulo é `flex-1` como no arquivo (`flex-[1_0_0]`): dimensionado pelo conteúdo ele
   * ficava 1px curto por arredondamento do `justify-between` e quebrava rótulos que cabiam.
   */
  icon?: string
  className?: string
}

export const CtaPill = ({
  title,
  path,
  external,
  onClick,
  variant,
  full,
  withArrow = true,
  icon,
  className
}: TCtaPillProps) => {
  const classes = cn(
    ctaPill({ variant, full }),
    icon && 'justify-between gap-8 whitespace-normal rounded-[16px] px-2 py-2 text-left',
    className
  )
  const content = (
    <>
      {icon ? (
        <span className="flex min-w-0 flex-1 items-center gap-2">
          <img src={icon} alt="" aria-hidden loading="lazy" decoding="async" className="size-12 shrink-0" />
          {title}
        </span>
      ) : (
        title
      )}
      {withArrow && <LuArrowRightCircle className={cn('h-4 w-4 shrink-0', icon && 'self-end')} aria-hidden />}
    </>
  )

  if (!path || path === '#') {
    return (
      <button type="button" onClick={onClick} className={cn(classes, 'cursor-pointer appearance-none')}>
        {content}
      </button>
    )
  }

  return (
    <Link
      to={path}
      isExternal={external}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      onClick={onClick}
      className={classes}>
      {content}
    </Link>
  )
}
